import { LoadingSpinner } from "@/components/ui/loading-spinner";
import type { ExportType } from "../types";

interface ExportCardProps {
  type: ExportType;
  title: string;
  description: string;
  /** When false, the card body renders a "Disabled by admin" line and no button. */
  enabled: boolean;
  isPending: boolean;
  error: string | null;
  onClick: (type: ExportType) => void;
  /** Forwarded to the underlying `<button>` so the modal can autofocus the
   * first card on open. Mirror of `ReassignOptionCard.buttonRef`. */
  buttonRef?: React.Ref<HTMLButtonElement>;
}

/**
 * One export-type card inside the Phase 6 ExportModal. Horizontal layout per
 * the prototype mockup: [icon-box] [title + description (flex:1)] [Download].
 *
 * The card primitive itself never hides — the MODAL decides whether to mount
 * the per-room card at all (it hides the card entirely when the manifest's
 * `perRoomEnabled` flag is false, since that flag is the admin-controlled
 * feature toggle).
 */
export function ExportCard({
  type,
  title,
  description,
  enabled,
  isPending,
  error,
  onClick,
  buttonRef,
}: ExportCardProps) {
  return (
    <div
      data-testid={`export-card-${type}`}
      className="bg-page border border-border-subtle rounded-md px-[14px] py-[13px]"
    >
      <div className="flex items-center gap-3">
        <div
          aria-hidden="true"
          className="w-[34px] h-[34px] rounded-[7px] bg-surface border border-border-subtle flex items-center justify-center text-[#60a5fa] shrink-0"
        >
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <div
            data-testid={`export-card-title-${type}`}
            className="text-[13px] font-medium text-text-soft"
          >
            {title}
          </div>
          <div
            data-testid={`export-card-description-${type}`}
            className="text-[12px] text-text-faint"
          >
            {description}
          </div>
        </div>
        {enabled ? (
          <button
            ref={buttonRef}
            type="button"
            onClick={() => onClick(type)}
            disabled={isPending}
            aria-label={`Download ${title}`}
            data-testid={`export-card-button-${type}`}
            className={[
              "inline-flex items-center gap-2 rounded-md px-[14px] py-[7px]",
              "bg-surface-2 hover:bg-surface-2/80 text-text-soft",
              "text-[13px] font-medium shrink-0",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand",
              isPending ? "opacity-60 cursor-not-allowed" : "cursor-pointer",
            ].join(" ")}
          >
            {isPending ? (
              <>
                <LoadingSpinner size="sm" />
                <span>Generating…</span>
              </>
            ) : (
              <span>Download</span>
            )}
          </button>
        ) : (
          <div
            data-testid={`export-card-disabled-${type}`}
            className="text-[12px] text-text-faint italic shrink-0"
          >
            Disabled by admin
          </div>
        )}
      </div>
      {error !== null && (
        <div
          data-testid={`export-card-error-${type}`}
          role="alert"
          className="text-[12px] text-red-400 mt-2"
        >
          {error}
        </div>
      )}
    </div>
  );
}
